/**
 * Auszeichnungen — Cinematic "Trophy Cabinet" (3× Lieferando).
 * Asymmetrische, überlappende Glas-Plaketten über tiefrotem Ambient-Glow.
 * Original-Logo unverfälscht, mit Jahr + Quelle (§5 UWG-konform).
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';

const AWARDS = [
  {
    id: '2024',
    title: 'Top bewertetes Restaurant',
    year: '2024',
    desktop: 'absolute left-0 top-12 rotate-[-6deg] scale-90',
    mobileRotate: '-2deg',
    z: 20,
  },
  {
    id: '2026',
    title: 'Top bewerteter Partner',
    year: '2026',
    desktop: 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
    mobileRotate: '2deg',
    z: 30,
    hero: true,
  },
  {
    id: '2023',
    title: 'Lieferando Award — Platz 3 Hamburg',
    year: '2023',
    desktop: 'absolute right-0 top-24 rotate-[6deg] scale-90',
    mobileRotate: '-2deg',
    z: 20,
  },
];

function Plaque({ award, hovered, setHovered, mobile = false }) {
  const isHovered = !mobile && hovered === award.id;
  const isDimmed = !mobile && hovered && !isHovered;

  const surface = (
    <motion.div
      className={`backdrop-blur-2xl bg-white/[0.02] border rounded-3xl p-8 flex flex-col items-center text-center shadow-2xl w-[320px] max-w-full cursor-default transition-colors duration-500 ${isHovered ? 'border-white/30' : 'border-white/10'}`}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: award.hero ? 0.1 : 0.3 }}
      animate={{
        scale: isHovered ? 1.05 : 1,
        opacity: isDimmed ? 0.3 : 1,
        filter: isDimmed ? 'grayscale(1)' : 'grayscale(0)',
      }}
      onMouseEnter={() => !mobile && setHovered(award.id)}
      onMouseLeave={() => !mobile && setHovered(null)}
      data-testid={`award-plaque-${award.id}${mobile ? '-mobile' : ''}`}
    >
      {isHovered && (
        <div className="absolute inset-0 rounded-3xl pointer-events-none" style={{ boxShadow: '0 0 30px rgba(212,175,55,0.15)' }} />
      )}
      {/* Watermark-Jahr */}
      <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white/[0.03] text-9xl font-black tracking-tighter z-0 pointer-events-none select-none">
        {award.year}
      </span>

      <div className="relative z-10 flex flex-col items-center gap-5 w-full">
        <img src="/images/award-laurel.png" alt="" className="w-24 h-24 object-contain opacity-90" loading="lazy" />
        <span className="font-mono text-[#D4AF37] text-sm tracking-[0.35em]">{award.year}</span>
        <h3 className="font-display text-2xl md:text-3xl text-white max-w-[250px] leading-snug tracking-tight">
          {award.title}
        </h3>

        <div className="mt-2 pt-6 border-t border-white/10 w-full flex flex-col items-center gap-3">
          <span className="text-[10px] uppercase tracking-wider text-white/50">Verliehen von</span>
          <img src="/images/lieferando-logo.png" alt="Lieferando" className="h-7 object-contain rounded" loading="lazy" data-testid="lieferando-logo" />
        </div>
      </div>
    </motion.div>
  );

  if (mobile) {
    return (
      <div className="relative mx-auto" style={{ transform: `rotate(${award.mobileRotate})`, zIndex: isHovered ? 50 : award.z }}>
        {surface}
      </div>
    );
  }
  return (
    <div className={award.desktop} style={{ zIndex: isHovered ? 50 : award.z }}>
      {surface}
    </div>
  );
}

export default function AwardsSection() {
  const [hovered, setHovered] = useState(null);

  return (
    <section
      className="relative w-full py-24 md:py-32 overflow-hidden bg-[#0C0A09]"
      aria-label="Auszeichnungen und Zertifikate"
      data-testid="awards-showcase"
    >
      {/* Ambient Brand-Red Glow */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#A81C1C] blur-[150px] opacity-20 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-8 items-center">
          {/* Text-Spalte */}
          <motion.div
            className="lg:col-span-5 flex flex-col gap-6"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#D4AF37]">
              Offizieller Partner · 3× ausgezeichnet
            </span>
            <h2 className="font-display text-5xl md:text-7xl tracking-tighter leading-none text-white">
              Ausgezeichnet<span className="text-[#A81C1C]">.</span>
            </h2>
            <p className="text-neutral-400 text-sm md:text-base leading-relaxed max-w-md">
              Drei Lieferando-Auszeichnungen in drei Jahren — für unsere Standorte
              in Rellingen &amp; Henstedt-Ulzburg. Qualität, die nicht nur unseren
              Kunden auffällt.
            </p>
            <div className="flex items-center gap-4 mt-2">
              <div className="h-px flex-1 max-w-[80px] bg-gradient-to-r from-[#D4AF37]/60 to-transparent" />
              <span className="font-mono text-white/30 text-xs tracking-widest">2023 — 2026</span>
            </div>
          </motion.div>

          {/* Showcase-Spalte: Desktop überlappend */}
          <div className="lg:col-span-7 relative h-[600px] w-full hidden md:block">
            {AWARDS.map(a => (
              <Plaque key={a.id} award={a} hovered={hovered} setHovered={setHovered} />
            ))}
          </div>

          {/* Mobile: vertikal gestapelt mit leichter Rotation */}
          <div className="flex flex-col gap-12 md:hidden">
            {AWARDS.map(a => (
              <Plaque key={a.id} award={a} hovered={hovered} setHovered={setHovered} mobile />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
